import {nodeColumn} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {
  TrustAccountMoreMenu,
  trustAccountBalanceColumn,
  trustAccountDateActionColumn,
  trustAccountNameColumn,
  trustAccountTypeColumn
} from '@/features/trust-accounts/components';

import {GuestInviterCell} from '../components';
import {GUEST_ACCOUNT_MENU_ACTIONS, GUEST_ACCOUNT_COLUMN_WIDTHS as WIDTHS} from '../config';
import {getGuestInviterName} from '../utils';

import type {GuestTrustAccountListItem} from '../utils';
import type {TrustAccountListColumn} from '@/features/trust-accounts/components';
import type {TFunction} from 'i18next';

type Row = GuestTrustAccountListItem;

/** Built once and shared by every firm's table, so the columns line up across the firm cards. */
export function useGuestAccountColumns(): TrustAccountListColumn<Row>[] {
  const {t} = useTranslation('guest');
  const {t: tAccounts} = useTranslation('trustAccounts');
  const navigate = useNavigate();
  return useMemo(
    () => buildGuestAccountColumns(t, tAccounts, row => navigate(row.path)),
    [t, tAccounts, navigate]
  );
}

function buildGuestAccountColumns(
  t: TFunction,
  tAccounts: TFunction,
  onOpen: (row: Row) => void
): TrustAccountListColumn<Row>[] {
  return [
    trustAccountNameColumn<Row>({header: tAccounts('columns.trustName'), width: WIDTHS.trustName}),
    trustAccountBalanceColumn<Row>({header: tAccounts('columns.balance'), width: WIDTHS.balance}),
    trustAccountTypeColumn<Row>({header: tAccounts('columns.type'), width: WIDTHS.type}),
    nodeColumn<Row>({
      id: 'invitedBy',
      header: t('accounts.row.invitedBy'),
      width: WIDTHS.invitedBy,
      accessorFn: getGuestInviterName,
      cell: row => <GuestInviterCell inviter={row.invitedBy} />
    }),
    trustAccountDateActionColumn<Row>({
      id: 'sharedAt',
      header: t('accounts.row.sharedOn'),
      width: WIDTHS.sharedAt,
      getCtaLabel: () => t('accounts.row.view'),
      onAction: onOpen,
      renderMenu: row => (
        <TrustAccountMoreMenu
          refId={row.refId}
          actions={GUEST_ACCOUNT_MENU_ACTIONS}
          orientation="vertical"
        />
      )
    })
  ];
}
